import { useState } from "react";
import type { PendingItemWithProvider } from "../../../../types";
import { useDeletePendingItem } from "../../api/useDeletePendingItem";
import { useGetPendingItemsBySubTopicId } from "../../api/useGetPendingItemsBySubTopicId";
import { usePostPendingItem } from "../../api/usePostPendingItem";
import { useUpdatePendingItem } from "../../api/useUpdatePendingItem";
import { usePermissions } from "../../../../hooks/usePermissions";

type AddParams = {
  title: string;
  providerName?: string | null;
  providerId?: string | null;
};

type EditParams = {
  id: number;
  title: string;
  providerName?: string | null;
  providerId?: string | null;
};

export function getPendingItemProviderLabel(item: PendingItemWithProvider) {
  return item.provider?.name ?? item.provider_name ?? null;
}

export function usePendingItemsSection(subTopicId: string) {
  const { isAdmin } = usePermissions();
  const { data: items = [], isLoading, isError } = useGetPendingItemsBySubTopicId(subTopicId, null);

  const { mutate: postItem, isPending: isAdding } = usePostPendingItem(subTopicId);
  const { mutate: updateItem, isPending: isUpdating } = useUpdatePendingItem(subTopicId);
  const { mutateAsync: deleteItem } = useDeletePendingItem(subTopicId);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<PendingItemWithProvider | null>(null);

  const addItem = ({ title, providerName, providerId }: AddParams) => {
    postItem({
      title,
      provider_name: providerName,
      provider_id: providerId,
      position: items.length,
    });
  };

  const editItem = ({ id, title, providerName, providerId }: EditParams) => {
    updateItem({
      id,
      title,
      provider_name: providerName,
      provider_id: providerId,
    });
  };

  const toggleItem = ({ id, completed }: { id: number; completed: boolean }) => {
    updateItem({
      id,
      state: completed ? 'completed' : 'pending',
      completed_at: completed ? new Date().toISOString() : null,
    });
  };

  function handleOpenAddModal() {
    setIsAddModalOpen(true);
  }

  function handleCloseAddModal() {
    setIsAddModalOpen(false);
  }

  function handleStartEdit(item: PendingItemWithProvider) {
    setEditingItem(item);
  }

  function handleCloseEdit() {
    setEditingItem(null);
  }

  function handleSaveEdit(params: { title: string; providerId: string | null; providerName: string | null }) {
    if (!editingItem) return;
    editItem({ id: editingItem.id, ...params });
  }

  const sortedItems = [...items].sort((a, b) => {
    // 1. Sort by state: 'pending' before 'completed'
    if (a.state !== b.state) {
      return a.state === 'pending' ? -1 : 1;
    }
    // 2. If same state, sort by created_at ascending (oldest first)
    return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
  });

  const completedCount = items.filter((item) => item.state === 'completed').length;

  return {
    isAdmin,
    items: sortedItems,
    isLoading,
    isError,
    isAdding,
    isUpdating,
    completedCount,
    isAddModalOpen,
    editingItem,
    addItem,
    toggleItem,
    deleteItem,
    handleOpenAddModal,
    handleCloseAddModal,
    handleStartEdit,
    handleCloseEdit,
    handleSaveEdit,
  };
}
